import { Icon } from '../icons';
import type { JobDoc, JobStatus } from '../../lib/types';

interface Props {
  job: JobDoc | null;
}

const STATUS_LABELS: Record<JobStatus, string> = {
  draft: 'מתכוננים',
  queued: 'בתור לעיבוד',
  scripting: 'בונים את הנרטיב בעברית',
  tts: 'מקליטים קריינות',
  rendering: 'מעבדים את הסרטון',
  ready: 'מוכן',
  paid: 'מוכן',
  failed: 'אירעה תקלה',
};

const STEP_ORDER: JobStatus[] = ['scripting', 'tts', 'rendering', 'ready'];

function stepIndex(status: JobStatus): number {
  if (status === 'draft' || status === 'queued') return -1;
  if (status === 'failed') return -2;
  const i = STEP_ORDER.indexOf(status);
  if (i >= 0) return i;
  if (status === 'paid') return STEP_ORDER.length - 1;
  return 0;
}

export function ProcessingScreen({ job }: Props) {
  const status = job?.status ?? 'queued';
  const progress = Math.max(0, Math.min(100, job?.progress ?? 0));
  const idx = stepIndex(status);

  return (
    <div className="screen">
      <div className="screen-header">
        <div style={{ width: 38 }} />
        <div className="step-counter">יוצרים את הסרטון</div>
        <div style={{ width: 38 }} />
      </div>
      <div className="processing-stage">
        <div className="proc-orbit">
          <div className="ring r2" />
          <div className="ring" />
          <div className="spin" />
          <div className="core">
            <Icon.Sparkles size={42} />
          </div>
        </div>
        <h1 className="h1" style={{ textAlign: 'center', fontSize: 26 }}>
          {status === 'failed' ? 'משהו השתבש' : 'הקסם קורה'}
        </h1>
        <p className="lede" style={{ textAlign: 'center' }}>
          {status === 'failed'
            ? job?.error?.message ?? 'אפשר לנסות שוב'
            : status === 'queued'
              ? 'מתחילים את העיבוד — אם זה נשאר כאן יותר מדקה, ייתכן ששרת הווידאו לא זמין.'
              : 'זה לוקח עד דקה. שווה את ההמתנה.'}
        </p>

        <div style={{ width: '100%', maxWidth: 280, margin: '0 auto' }}>
          <div
            style={{
              height: 4,
              background: 'oklch(0.62 0.13 45 / 0.15)',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                background: 'var(--accent)',
                borderRadius: 2,
                transition: 'width .2s',
              }}
            />
          </div>
          <div
            style={{
              marginTop: 8,
              fontFamily: 'Heebo',
              fontSize: 13,
              color: 'var(--ink-mute)',
              textAlign: 'center',
            }}
          >
            {Math.round(progress)}% · {STATUS_LABELS[status]}
          </div>

          <div className="proc-steps" style={{ marginTop: 24 }}>
            {STEP_ORDER.map((s, i) => (
              <div
                key={s}
                className={'proc-step ' + (i < idx ? 'done' : i === idx ? 'active' : '')}
              >
                <div className="dot">
                  {i < idx ? (
                    <Icon.Check size={11} />
                  ) : i === idx ? (
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: 999,
                        background: 'var(--accent)',
                      }}
                    />
                  ) : null}
                </div>
                <span>{STATUS_LABELS[s]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
